'use client';

import { useEffect, useState } from 'react';

import { type AddressSuggestion, fetchAddressSuggestions } from '@/lib/address';

const DEBOUNCE_MS = 200;

function generateSessionToken(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface UseAddressAutocompleteResult {
  suggestions: AddressSuggestion[];
  isLoading: boolean;
  error: Error | null;
  sessionToken: string;
}

export function useAddressAutocomplete(query: string): UseAddressAutocompleteResult {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [sessionToken] = useState(generateSessionToken);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSuggestions([]);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const run = async () => {
      if (cancelled) return;
      setIsLoading(true);
      setError(null);
      try {
        const results = await fetchAddressSuggestions(trimmed, sessionToken, controller.signal);
        if (!cancelled) {
          setSuggestions(results);
        }
      } catch (err) {
        if (!cancelled && err instanceof Error && err.name !== 'AbortError') {
          setError(err);
          setSuggestions([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };
    const timeoutId = window.setTimeout(() => {
      void run();
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query, sessionToken]);

  return { suggestions, isLoading, error, sessionToken };
}
